import { createContext } from 'react';

import Api from 'api';
import { getEntryPointsFromStorages, getReferrer } from 'utils';
import { showModal } from 'components/Modals';

export const createInitialValues = fields => {
  const result = {};

  for (const fieldKey in fields) {
    if (fields[fieldKey].initialValue === undefined) {
      throw new TypeError(
        `The settings object of field "${fieldKey}" must contain "initialValue" property!`,
      );
    }

    result[fieldKey] = fields[fieldKey].initialValue;
  }

  return result;
};

export const createFields = (fields, nda) => {
  const result = {};
  const fieldsArray = Object.entries(fields);

  for (let i = 0; i < fieldsArray.length; i++) {
    result[fieldsArray[i][0]] = {
      ...fieldsArray[i][1],
      name: fieldsArray[i][0],
      order: i,
    };
  }

  if (nda) {
    result.nda = {
      name: 'nda',
      initialValue: Boolean(nda.initialValue),
      top: Boolean(nda.top),
      order: fieldsArray.length,
    };
  }

  return result;
};

const prepareFormData = async (
  formName,
  values,
  additionalFormData,
  getAdditionalFormData,
  withoutCaptchaToken,
) => {
  let data = {
    form: formName,
    ...values,
    ...additionalFormData,
    ...getEntryPointsFromStorages(),
    lastPoint: window.location.href,
    referrer: getReferrer(),
  };

  if (typeof getAdditionalFormData === 'function') {
    data = { ...data, ...getAdditionalFormData() };
  }

  if (!withoutCaptchaToken) {
    data.captchaToken = await Api.getCaptchaToken();
  }

  return data;
};

const handleSubmitSuccess = ({
  onSuccess,
  onResponse,
  pushSubmitEvent,
  formName,
  successModal,
  reset,
  resetHandler,
  setStatus,
  data,
}) => {
  const successActions = [
    { condition: typeof onSuccess === 'function', action: () => onSuccess() },
    { condition: typeof onResponse === 'function', action: () => onResponse() },
    {
      condition: typeof pushSubmitEvent === 'function',
      action: () => pushSubmitEvent(formName),
    },
    { condition: successModal, action: () => showModal(successModal, data) },
    { condition: reset, action: () => resetHandler() },
  ];

  successActions.forEach(({ condition, action }) => {
    if (condition) action();
  });

  setStatus('success');
};

const handleSubmitError = ({ onError, onResponse, errorModal, setStatus }) => {
  const errorActions = [
    { condition: typeof onError === 'function', action: () => onError() },
    { condition: typeof onResponse === 'function', action: () => onResponse() },
    { condition: errorModal, action: () => showModal(errorModal) },
  ];

  errorActions.forEach(({ condition, action }) => {
    if (condition) action();
  });

  setStatus();
};

export const submitHandler = async ({
  id,
  formName,
  values,
  handler,
  onSuccess,
  successModal,
  errorModal,
  onError,
  onResponse,
  reset: { reset, resetHandler },
  setStatus,
  additionalFormData,
  getAdditionalFormData,
  withoutCaptchaToken,
  pushSubmitEvent,
}) => {
  const data = await prepareFormData(
    formName,
    values,
    additionalFormData,
    getAdditionalFormData,
    withoutCaptchaToken,
  );

  try {
    await handler(data);
    handleSubmitSuccess({
      onSuccess,
      onResponse,
      pushSubmitEvent,
      formName,
      successModal,
      reset,
      resetHandler,
      setStatus,
      data: successModal === 'SuccessModal' && { ...values },
    });
  } catch (e) {
    handleSubmitError({ onError, onResponse, errorModal, setStatus });
  }
};

export const findFirstFieldWithError = (fields, errors) => {
  let min = null;
  let targetFieldName = null;
  const fieldsWithError = Object.keys(errors);

  for (const fieldKey in fields) {
    if (fieldsWithError.includes(fieldKey)) {
      if (min === null || min > fields[fieldKey].order) {
        min = fields[fieldKey].order;
        targetFieldName = fieldKey;
      }
    }
  }

  return targetFieldName;
};

export const filterValidationRules = rules => {
  const newRules = {};

  for (const rulesKey in rules) {
    if (document.querySelector(`[name=${rulesKey}]`)) {
      newRules[rulesKey] = rules[rulesKey];
    }
  }

  return newRules;
};

export const ErrorsShakeHubContext = createContext();
export const ThemeContext = createContext();
